'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/shared/lib/toast';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { supportService } from '../api/support.service';
import {
  MAX_MESSAGE_LENGTH,
  MIN_MESSAGE_LENGTH,
  type FeedbackCategory,
} from '../types';
import { useTranslations } from 'next-intl';
import { AttachmentPicker, type AttachmentItem } from './AttachmentPicker';

function createFeedbackSchema(t: ReturnType<typeof useTranslations>) {
  return z.object({
    category: z.enum([
      'general_inquiry',
      'technical_support',
      'billing_issue',
      'feature_request',
      'bug_report',
      'account_issue',
      'other',
    ] as const),
    // Subject is no longer entered by the user — the API derives it from the
    // message server-side. The field is kept only to carry the `initialSubject`
    // seed (e.g. Pro Plan / pro inquiry) straight through to the payload.
    subject: z.string().optional(),
    message: z
      .string()
      .min(MIN_MESSAGE_LENGTH, t('message.errors.minLength', { min: MIN_MESSAGE_LENGTH }))
      .max(MAX_MESSAGE_LENGTH, t('message.errors.maxLength', { max: MAX_MESSAGE_LENGTH })),
  });
}

type FeedbackFormData = z.infer<ReturnType<typeof createFeedbackSchema>>;

export function FeedbackForm({
  initialSubject = '',
}: {
  /**
   * Seeds the subject when the form is opened from a specific entry point (e.g.
   * the "for pros" Pro Plan inquiry from the Packs storefront) so the
   * support ticket is recognizable; the user can still edit it. Mirrors Apple's
   * `FeedbackView(initialSubject:)`.
   */
  initialSubject?: string;
} = {}) {
  const t = useTranslations('feedback.form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string>('');
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);

  const anyUploading = attachments.some((a) => a.phase === 'uploading');
  const readyAttachmentIds = attachments
    .filter((a): a is AttachmentItem & { remoteId: string } =>
      a.phase === 'ready' && !!a.remoteId
    )
    .map((a) => a.remoteId);

  const feedbackSchema = useMemo(() => createFeedbackSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      category: 'bug_report',
      subject: initialSubject,
      message: '',
    },
  });

  const messageLength = watch('message')?.length || 0;

  const onSubmit = async (data: FeedbackFormData) => {
    // Rate limiting: prevent spam clicks (3 second cooldown)
    const now = Date.now();
    if (now - lastSubmitTime < 3000) {
      toast.error(t('errors.rateLimited'));
      return;
    }

    // Mirror the UI-level gate: if any attachment is still uploading, refuse
    // to submit so the ticket isn't sent with a silently-dropped attachment list.
    if (anyUploading) {
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const response = await supportService.submitFeedback({
        ...data,
        attachment_ids: readyAttachmentIds.length > 0 ? readyAttachmentIds : undefined,
      });

      // Update state with success
      setSubmitSuccess(true);
      setTicketNumber(response.data.attributes.ticket_number);
      setLastSubmitTime(now);

      // Show success toast
      toast.success(t('success.toast'));

      // Reset form after successful submission
      reset();
      setAttachments([]);

      // Hide success message after 10 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setTicketNumber('');
      }, 10000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error(
        error instanceof Error ? error.message : t('errors.submitFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {submitSuccess && ticketNumber && (
        <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {t('success.title')} {t('success.message', { ticketNumber })}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="category">{t('category.label')}</Label>
        <Select
          onValueChange={(value) =>
            setValue('category', value as FeedbackCategory)
          }
          defaultValue="bug_report"
        >
          <SelectTrigger id="category" className="w-full">
            <SelectValue placeholder={t('category.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general_inquiry">
              {t('category.generalInquiry')}
            </SelectItem>
            <SelectItem value="technical_support">
              {t('category.technicalSupport')}
            </SelectItem>
            <SelectItem value="billing_issue">
              {t('category.billingIssue')}
            </SelectItem>
            <SelectItem value="feature_request">
              {t('category.featureRequest')}
            </SelectItem>
            <SelectItem value="bug_report">
              {t('category.bugReport')}
            </SelectItem>
            <SelectItem value="account_issue">
              {t('category.accountIssue')}
            </SelectItem>
            <SelectItem value="other">{t('category.other')}</SelectItem>
          </SelectContent>
        </Select>
        {errors.category && (
          <p className="text-form-error text-red-500">{errors.category.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="message">{t('message.label')}</Label>
          <span className="text-form-help text-muted-foreground">
            {messageLength} / {MAX_MESSAGE_LENGTH}
          </span>
        </div>
        <Textarea
          id="message"
          {...register('message')}
          placeholder={t('message.placeholder')}
          rows={10}
          disabled={isSubmitting}
          maxLength={MAX_MESSAGE_LENGTH}
          className="resize-none min-h-[180px]"
        />
        {errors.message && (
          <p className="text-form-error text-red-500">{errors.message.message}</p>
        )}
      </div>

      <AttachmentPicker
        attachments={attachments}
        onChange={setAttachments}
        disabled={isSubmitting}
      />

      <div className="flex items-center justify-end gap-4">
        {!isSubmitting && !submitSuccess && (
          <Button type="button" variant="ghost" onClick={() => reset()}>
            {t('actions.clear')}
          </Button>
        )}

        <Button
          type="submit"
          disabled={isSubmitting || submitSuccess || anyUploading}
          className="min-w-[120px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
              {t('actions.submitting')}
            </>
          ) : submitSuccess ? (
            <>
              <CheckCircle2 className="me-2 h-4 w-4" />
              {t('actions.submitted')}
            </>
          ) : (
            t('actions.submit')
          )}
        </Button>
      </div>

      <p className="text-form-help text-muted-foreground text-center">
        {t('reassurance')}
      </p>
    </form>
  );
}
